/**
 * agentloop.mjs — who is driving the iteration, and what stops it.
 *
 * FOUNDER, 2026-08-14: "ai101 labs next" → chapter 4.
 *
 * ── THE LINE THE CHAPTER HANGS ON ─────────────────────────────────────────────────────────────
 * §1: "Hold this one line: a prompt asks once and stops; an agent keeps going until it thinks it's
 * done." And the distinction underneath it: "In a prompt, the human drives. In an agent, the AI
 * drives." So the lab makes the learner BE the loop first — press next, press next, press next —
 * and then hands the loop over and watches it go without them. The difference is felt in the
 * fingers before it is understood.
 *
 * ── AND THE ONE THAT COSTS MONEY ──────────────────────────────────────────────────────────────
 * §5: "The most common cause of failed agent deployments is not the AI being bad at the task. It is
 * the loop never terminating." Four boundaries — goal, attempts, cost, time — and his verdict:
 * "When all four boundaries are in place, an agent that fails fails cheaply. When any of them are
 * missing, agent failure is expensive." Each is a switch here, and turning one off is the
 * demonstration.
 *
 * ── NO PRICE IS INVENTED ──────────────────────────────────────────────────────────────────────
 * He says a confused agent can burn "hundreds of rupees (or hundreds of dollars) in minutes" and
 * gives no per-call figure — deliberately, since it varies by model and by tool. So this engine
 * counts CALLS, which is his own unit for the attempt limit ("Claude Code defaults to ~25-50 tool
 * calls per task"), and any money on screen is priced by a number the LEARNER supplies. A lab that
 * printed "₹4.20 per call" would be inventing a commercial fact in a course about not doing that.
 *
 * ── THE LAB'S OWN LOOP IS BOUNDED, AND SAYS SO ────────────────────────────────────────────────
 * A lab about unterminating loops that could hang a browser would be the joke that writes itself,
 * as the shell one would have been in chapter 3. There is a hard ceiling no setting can raise, and
 * hitting it is not presented as the agent finishing — it is presented as THIS PAGE stopping,
 * which is the honest difference between a demonstration and the thing demonstrated.
 */

export const SOURCE_1 = 'CodeAI101 module 04 §1 "The two shapes of AI use — one-shot vs looping"';
export const SOURCE_3 = 'CodeAI101 module 04 §3 "The autonomy spectrum — where on the dial?"';
export const SOURCE_5 = 'CodeAI101 module 04 §5 "Goals, attempts, limits, termination — keeping agents bounded"';

export const THE_LINE = 'a prompt asks once and stops; an agent keeps going until it thinks it\'s done.';
export const WHO_DRIVES = 'Both shapes use the same underlying language model. The difference is who is driving the iteration. In a prompt, the human drives. In an agent, the AI drives.';
export const YOU_ARE_THE_LOOP = 'The AI does one thing per turn. You are the loop.';
export const AI_IS_THE_LOOP = 'running on its own — until it either achieves the goal or hits a stopping condition. You see the final result and possibly a summary of what happened. The AI is the loop.';

/** His two shapes, as he draws them. */
export const SHAPES = Object.freeze({
  prompt: { name: 'Prompt', arrow: 'Prompt → AI response → you decide → next prompt → AI response → ...', driver: 'you' },
  agent: { name: 'Agent', arrow: 'You set goal → AI plans → AI calls tool → result → AI plans → AI calls tool → ... → AI reports back', driver: 'the AI' },
});

/** §3's three points on the dial, quoted. */
export const DIAL = Object.freeze([
  { id: 'stepwise', n: 1, name: 'Step-by-step approval',
    quote: 'The agent proposes the next action; you approve each one before it runs. Slow, safe, useful for high-stakes work — running production database migrations, sending official communications, taking actions that cannot be undone.' },
  { id: 'monitored', n: 2, name: 'Auto-run with monitoring',
    quote: 'The agent runs its loop without per-step approval, but you are watching — logs streaming, dashboards updating, you can interrupt at any time.' },
  { id: 'autonomous', n: 3, name: 'Fully autonomous',
    /* Including his "Used for:" sentence, which the first version jumped over — two non-adjacent
       sentences joined with no ellipsis, the same fault as chapter 2's system message. */
    quote: 'The agent runs without human supervision at all, on a schedule or in response to events. Used for: monitoring systems, background data processing, automated customer responses. The most dangerous mode if not bounded carefully; the most powerful if it is.' },
]);
export const CALIBRATION = 'High-stakes, irreversible, expensive → toward the manual end. Low-stakes, reversible, cheap, repetitive → toward the autonomous end.';
export const WHERE_MOST_LIVE = 'Most agents in 2026 live between point 1 and point 2 because that is where most useful work happens; point 3 is where the most valuable and the most dangerous agents live.';

/** §5's four boundaries, quoted. Each is a switch in the lab. */
export const BOUNDS = Object.freeze([
  { id: 'goal', name: 'A clear goal',
    quote: 'Vague goals produce wandering agents. "Improve our customer support" will not converge. "Draft an email reply to ticket #4823 based on the customer\'s three previous tickets and our refund policy" will. Goals should have a measurable end state.' },
  { id: 'attempts', name: 'An attempt limit',
    quote: 'Maximum number of tool calls. If the agent hits the limit without succeeding, it stops and reports back.' },
  { id: 'cost', name: 'A cost limit',
    quote: 'Every tool call usually costs money — API fees, compute, third-party tool fees. Without a cost cap, a confused agent can burn through hundreds of rupees (or hundreds of dollars) in minutes.' },
  { id: 'time', name: 'A time limit', quote: 'Wall-clock maximum. Useful when the agent can hang on slow tool responses or stuck network calls.' },
]);
export const WHY_IT_FAILS = 'The most common cause of failed agent deployments is not the AI being bad at the task. It is the loop never terminating — the agent keeps trying, keeps spending, keeps producing output, until somebody notices and kills it.';
export const CHEAP_OR_EXPENSIVE = 'When all four boundaries are in place, an agent that fails fails cheaply. When any of them are missing, agent failure is expensive.';
/* The whole parenthetical. My first version started mid-bracket and closed with a full stop the
   module does not have there — third elision caught today. */
export const HIS_DEFAULT = 'Usually configurable; defaults exist (Claude Code defaults to ~25-50 tool calls per task; Codex similar).';
export const DEFAULT_ATTEMPTS = 25;   // the low end of his own figure

/**
 * A HARD CEILING NO SETTING CAN RAISE. The page must not be able to loop forever while teaching
 * that loops must not run forever. Reaching it is reported as the LAB stopping, never as the agent
 * finishing — those are different events and conflating them would teach that something always
 * catches a runaway. In production nothing does; that is §5's entire point.
 */
export const LAB_CEILING = 200;

/**
 * Run the loop. `converges` decides whether the goal is reachable at all — a vague goal never
 * converges, which is his first boundary made mechanical rather than described.
 *
 * Cost is counted in CALLS and multiplied by a price the caller supplies. Pass no price and no
 * money appears anywhere.
 */
export function runAgent({ bounds = { goal: true, attempts: true, cost: true, time: true },
  attemptLimit = DEFAULT_ATTEMPTS, costLimit = 20, timeLimit = 12, pricePerCall = null } = {}) {
  /* A clear goal converges — he says so: a measurable end state is reachable. A vague one wanders,
     and wandering is not slow success, it is non-termination. */
  const convergesAt = bounds.goal ? 7 : null;
  const trace = [];
  let stopped = null;

  for (let call = 1; call <= LAB_CEILING; call += 1) {
    if (bounds.attempts && call > attemptLimit) { stopped = { by: 'attempts', at: call - 1 }; break; }
    if (bounds.cost && call > costLimit) { stopped = { by: 'cost', at: call - 1 }; break; }
    if (bounds.time && call > timeLimit) { stopped = { by: 'time', at: call - 1 }; break; }
    trace.push({ call, note: convergesAt && call >= convergesAt ? 'goal reached' : 'still trying' });
    if (convergesAt && call >= convergesAt) { stopped = { by: 'goal', at: call }; break; }
    if (call === LAB_CEILING) { stopped = { by: 'lab-ceiling', at: call }; }
  }

  const calls = trace.length;
  return {
    trace, calls,
    stoppedBy: stopped?.by ?? 'lab-ceiling',
    /* Succeeded ONLY if the goal was reached. Stopping on a limit is not success and must never be
       reported as one — his sentence is "it stops and reports back", not "it finishes". */
    succeeded: stopped?.by === 'goal',
    /* The lab ran out, which is not the same as anything stopping it in the real world. */
    labStopped: stopped?.by === 'lab-ceiling',
    cost: pricePerCall == null ? null : Math.round(calls * pricePerCall * 100) / 100,
    boundsOff: Object.entries(bounds).filter(([, on]) => !on).map(([k]) => k),
  };
}

/** His verdict on a failure, decided by whether every boundary was in place. */
export const verdictFor = (r, bounds) =>
  r.succeeded ? 'reached the goal'
    : Object.values(bounds).every(Boolean) ? 'failed cheaply' : 'failed expensively';

/**
 * ── THE FOUR ARE NOT THE SAME KIND OF THING, AND THE LAB SAYS SO ──────────────────────────────
 * Running it makes a distinction visible that his list does not draw: a VAGUE GOAL decides whether
 * the agent can ever finish, while attempts, cost and time only decide how much it costs when it
 * cannot. Turn off the goal but keep the other three and the loop still stops at twelve calls —
 * cheap by any measure — while his rule, applied literally, calls that "failed expensively".
 *
 * That is not a contradiction to correct. It is the difference between a CONVERGENCE boundary and
 * three DAMAGE CAPS, and it sharpens his own sentence rather than arguing with it: the caps are
 * what make a wandering agent affordable, and the goal is what stops it wandering. The lab prints
 * his verdict AND the actual call count side by side so a learner can see both and hold the
 * distinction, instead of reading "expensively" beside twelve calls and quietly distrusting one of
 * them.
 */
export const KIND_OF = Object.freeze({
  goal: 'convergence — decides whether it can ever finish',
  attempts: 'damage cap — decides how much it costs when it cannot',
  cost: 'damage cap — decides how much it costs when it cannot',
  time: 'damage cap — decides how much it costs when it cannot',
});
